import type { JsonClassification } from '@/features/sql-resolver/types/schema';

export type JsonColumnGuessOptions = {
  maxRowsToSample?: number;
  maxParseAttempts?: number;
  minSuccessfulParses?: number;
};

/**
 * Column-metadata path for the Result Grid JSON detection (JCS § Slice E1).
 * Returns `true` when the column carries an explicit non-`not-json`
 * classification — the grid should auto-format such cells as JSON.
 */
export function isJsonClassifiedColumn(
  jsonClassification: JsonClassification | undefined
): boolean {
  if (!jsonClassification) return false;
  return jsonClassification.kind !== 'not-json';
}

/**
 * Returns `true` when the column carries an explicit `{ kind: 'not-json' }`
 * classification. The Result Grid uses this to suppress the heuristic
 * auto-format and instead surface a "Looks like JSON" tooltip on cells whose
 * value still looks JSON-shaped (E1 value-shape fallback).
 */
export function isExplicitlyNotJsonColumn(
  jsonClassification: JsonClassification | undefined
): boolean {
  return jsonClassification?.kind === 'not-json';
}

const DEFAULT_GUESS_OPTIONS: Required<JsonColumnGuessOptions> = {
  maxRowsToSample: 50,
  maxParseAttempts: 10,
  minSuccessfulParses: 3,
};

export function isStringDataType(dataType: string | undefined): boolean {
  if (!dataType) return false;
  const normalized = dataType.trim().toLowerCase().split('(')[0] || '';

  // MSSQL / generic
  if (['varchar', 'nvarchar', 'char', 'nchar', 'text', 'ntext', 'xml'].includes(normalized)) return true;

  // PostgreSQL
  if (['text', 'varchar', 'bpchar', 'name', 'citext'].includes(normalized)) return true;

  return false;
}

export function looksLikeJsonString(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length < 2) return false;

  const start = trimmed[0];
  const end = trimmed[trimmed.length - 1];

  const isObject = start === '{' && end === '}';
  const isArray = start === '[' && end === ']';

  return isObject || isArray;
}

export function guessJsonColumn(
  rows: Record<string, unknown>[],
  columnKey: string,
  options: JsonColumnGuessOptions = {}
): boolean {
  const { maxRowsToSample, maxParseAttempts, minSuccessfulParses } = {
    ...DEFAULT_GUESS_OPTIONS,
    ...options,
  };

  let parseAttempts = 0;
  let successes = 0;

  for (let i = 0; i < rows.length && i < maxRowsToSample; i++) {
    if (successes >= minSuccessfulParses) return true;
    if (parseAttempts >= maxParseAttempts) return false;

    const value = rows[i]?.[columnKey];
    if (typeof value !== 'string') continue;
    if (!looksLikeJsonString(value)) continue;

    parseAttempts += 1;
    try {
      JSON.parse(value);
      successes += 1;
    } catch {
      // Ignore invalid JSON strings
    }
  }

  return successes >= minSuccessfulParses;
}

